import React, {useLayoutEffect, useRef} from 'react';
import {componentInit, tailCheck, useObserver, useRerender} from './CommonReact';
import {classNameScrollbar, remPixels, tooltipIfNeeded} from './CommonHtml';
import {funcOrValOr, isInRange} from './CommonJS';

// cols: [{ h?: header, f?: field, d?(index, iCol, col, rowData, cellData): display func, className?, classNameHeader?, props?, noSort?: header not clickable }]

export function UIVirtualTable(
		{
			rows, styleHeight = undefined, colGap = 0.25, cols = undefined, headerRenderer = undefined, colHeaderRenderer = undefined,
			colGrow = undefined, colPx = undefined, colMinPx = undefined, colFlex = undefined,
			rowRenderer = undefined, oInterface = undefined,
			onRowClick = undefined, onRowDoubleClick = undefined, onContext = undefined, onClickCell = undefined,
			onMouseEnterCell = undefined, onSizing = undefined, onClickHeader = undefined, overscanRowCount = 4, scrollToIndex = undefined,
			autoHeight = false,
			className = undefined, classNameGrid = undefined, classNameRow = undefined, classNameCol = undefined, propsRow = undefined,
			dataTest = undefined, emptyVal = undefined
		}) {
	const rerender = useRerender();
	const vals = useRef({startIndex: 0, stopIndex: 0, hTm: {}, pxRow: 0, measured: false}).current;
	const rTbl = useRef(), rHeader = useRef(), rMeasureRow = useRef();
	Object.assign(vals, {rows});

	function calcSizes(cmd, scrollToIndex) {
		if (!rTbl.current) return;          // onScroll can evidently be called even after ref gets cleared.
		const pxRow = vals.pxRow;
        // scrollToPx recurses into calcSizes so startIndex/stopIndex re-derive against the new scrollTop within the same commit.
		const scrollToPx = (px) => { rTbl.current.scrollTop = px; calcSizes(cmd); };
		if (!pxRow)
			return onSizing?.({gridRect: rTbl.current.getBoundingClientRect(),
				startIndex: 0,
				stopIndex: 0,
				fRowsFit: 0,
				overscanRowCount,
				elGrid: rTbl.current,
				sclBarPx: 0,
				scrollTop: rTbl.current.scrollTop,
				scrollToPx});
		const gridRect = rTbl.current.getBoundingClientRect();
		const fRowsFit = gridRect.height / pxRow;
		let fIdxWinTop = rTbl.current.scrollTop / pxRow, newTop;

		if (scrollToIndex < fIdxWinTop)
			fIdxWinTop = newTop = scrollToIndex;
		else if (scrollToIndex >= fIdxWinTop + (fRowsFit | 0))
			fIdxWinTop = newTop = scrollToIndex - Math.max(fRowsFit - 1 | 0, 0);

		if (newTop >= 0)
			(cmd !== 'inRender')? rTbl.current.scrollTop = newTop * pxRow :
				setTimeout(() => { if (rTbl.current) rTbl.current.scrollTop = newTop * pxRow; });

		vals.startIndex = Math.max(fIdxWinTop - overscanRowCount/2 |0, 0);     // have half the overscan rows before visible view
		vals.stopIndex = Math.ceil(fIdxWinTop + fRowsFit + overscanRowCount / 2);

		const cs = getComputedStyle(rTbl.current);
		const sclBar = rTbl.current.offsetWidth - rTbl.current.clientWidth;
		vals.sclBarWidth = sclBar + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);

		if (!(vals.msLastScrollRecalc + 1000 > Date.now()) &&
            (vals.rows?.length >= fRowsFit) !== !!sclBar) {     // if bar needed but not there - recalc after bar appears...
			vals.msLastScrollRecalc = Date.now();                         // avoid re-layout loops
			vals.hTm.recalc = setTimeout(calcSizes);
		}

		const doOnSizing = () => onSizing?.({gridRect,
			startIndex: vals.startIndex,
			stopIndex: vals.stopIndex,
			fRowsFit,
			overscanRowCount,
			elGrid: rTbl.current,
			sclBarPx: sclBar,
			scrollTop: rTbl.current.scrollTop,
			scrollToPx});

		if (cmd !== 'inRender') {
			doOnSizing();
			rerender();
		} else if (onSizing)
			vals.hTm.sz = setTimeout(doOnSizing);
	}

	useObserver(ResizeObserver, rTbl, 'height', () => {
		if (vals.measured && vals.rows?.length) {
			const [row0, row1] = rTbl.current?.children?.[0]?.children ?? [];
			const h = row1 ? row1.getBoundingClientRect().top - row0.getBoundingClientRect().top : 0;     // Row-to-row distance is the true row pitch.
			if (h > 0 && Math.abs(h - vals.pxRow) > 0.5)
				vals.pxRow = h;
		}
		calcSizes('ResizeObserver');
	}, {debounceMs: 100, noInit: 1});     // noInit is safe: the layout effect already calculates sizes when the table mounts.

	useLayoutEffect(() => {
		if (!vals.measured && rMeasureRow.current) {
			const h = parseFloat(getComputedStyle(rMeasureRow.current).height);     // Flow-root keeps row margins in the box; computed height ignores scale.
			if (h > 0) {
				vals.pxRow = h;
				vals.measured = true;
				rerender();
			}
		}
	});

	useLayoutEffect(() => {
		if (!rTbl.current) return;
		if (rTbl.current.scrollHeight)
			calcSizes('layoutEffect');
		if (oInterface) Object.assign(oInterface, {
			getVisibleRowElements: () => {
				const body = rTbl.current?.children[0];
				if (!body) return null;
				return {elements: [...body.children], index: vals.startIndex};
			}
		});
		return () => Object.values(vals.hTm).forEach(clearTimeout);
	}, [rTbl.current, rows]);

	if (rTbl.current && isInRange(scrollToIndex, 0, rows?.length -1) && componentInit(vals, [scrollToIndex], '_initScroll'))
		calcSizes('inRender', scrollToIndex);

    // Row-height measurement uses the first LOADED row: with virtual paging, rows is sparse (sized by rowCount), so rows[0] can
    // be a hole while a later chunk is present, and rowRenderer dereferences rowData (selection selectors, column d funcs).
	const iMeasure = vals.measured? -1 : rows?.findIndex(r => r !== undefined) ?? -1;

	const pxGap = colGap * remPixels();
	const totalColWidth = colPx && cols ? cols.reduce((sum, c, i) =>
		sum + (colFlex?.[i]?.startsWith('0') !== false ? colPx[i] : (colMinPx?.[i] || 10)), 0) + pxGap * (cols.length - 1) : 0;
	const style = {minWidth: totalColWidth + 'px'};
	const rowStyle = {display: 'flex', whiteSpace: 'nowrap', overflow: 'hidden', gap: colGap+'rem', alignItems: 'center'};
	const colStyles = cols?.map((c, iCol) => ({
		flex: colFlex?.[iCol] ?? (c.flex ?? `${c.wConst? 0 : colGrow?.[iCol] ?? c.w ?? 1} 0 ${c.wConst? c.wConst+'rem' : '0'}`),
		minWidth: colMinPx?.[iCol] || 0,
		...c.style
	}));

	if (!rowRenderer) rowRenderer = ({style, ...passParams}) => (
		<div
			key={passParams.index} className={funcOrValOr(classNameRow, passParams, '')}
			style={{...style, ...rowStyle}}
			onClick={onRowClick && (event => onRowClick({event, ...passParams}))}
			onDoubleClick={onRowDoubleClick && (event => onRowDoubleClick({event, ...passParams}))}
			onContextMenu={onContext && (event => { event.preventDefault(); onContext({event, ...passParams}); })}
			{...propsRow}>
			{cols.map((col, iCol) => {
				const cellData = passParams.rowData[col.f];
				const cellParams = {...passParams, iCol, col, cellData};
				return (
					<div
						key={iCol}
						style={{...colStyles[iCol], ...col.cellStyle}}
						className={`${funcOrValOr(classNameCol, cellParams, '')} ${col.className}`}
						onMouseEnter={col.onMouseEnter || onMouseEnterCell?
							(event => (col.onMouseEnter || onMouseEnterCell)({event, ...cellParams})) : tooltipIfNeeded}
						onClick={!onClickCell? undefined : event => onClickCell({event, ...cellParams})}
						{...col.props}>
						{col.d ? col.d(cellParams) : cellData}
					</div>
				);
			})}
		</div>
	);

	return (
		<div
			{...styleHeight && {style: {height: `${styleHeight}px`}}}
			className={`flex flex-col w-full ${!autoHeight && !className?.match(/(^| )h-/) && 'h-full'} ${className}`}>
			<div className='flex items-center'>
				<div ref={rHeader} style={autoHeight ? {flexGrow: 1, flexShrink: 0, minWidth: totalColWidth + 'px'} : {overflow: 'hidden', flex: '1 1 0', minWidth: 0}}>
					{cols && (
						headerRenderer?
							headerRenderer({cols, style, rowStyle, colStyles})
							: <div style={{flexShrink: 0, ...style, ...rowStyle}}>
								{cols.map((col, iCol) => (
									<div
										key={iCol} style={{...colStyles[iCol], ...onClickHeader && !col.noSort && {cursor: 'pointer'}}} className={col.classNameHeader}
										onClick={onClickHeader && !col.noSort && (event => onClickHeader({event, iCol, col}))}>
										{colHeaderRenderer? React.createElement(colHeaderRenderer, {iCol, col}) : col.h}
									</div>
								))
								}
							</div>
					)}
				</div>
				{!autoHeight && !!vals.sclBarWidth && <div style={{width: `${vals.sclBarWidth}px`, flexShrink: 0}}/>}
			</div>
			<div
				data-test={`${dataTest}-ui-virtual-table`} ref={rTbl}
				className={autoHeight
					? tailCheck(classNameGrid, 'shrink-0', 'w-full', 'overflow-visible', 'relative')
					: tailCheck(classNameGrid ?? classNameScrollbar, 'grow', 'w-full', 'overflow-auto', 'relative')}
				onScroll={e => {
					calcSizes('onScroll');
					if (rHeader.current)
						rHeader.current.scrollLeft = rTbl.current.scrollLeft;
				}}>
				{autoHeight?
					<div style={{minWidth: totalColWidth + 'px'}}>
						{rows?.length? rows.map((rowData, index) => rowData === undefined?
							<div key={index} style={style}/>
							:
							rowRenderer({style, rowData, index}))
							:
							funcOrValOr(emptyVal)
						}
					</div>
					: iMeasure >= 0?
						<div ref={rMeasureRow} style={{minWidth: totalColWidth + 'px', display: 'flow-root'}}>
							{rowRenderer({style, rowData: rows[iMeasure], index: iMeasure})}
						</div>
						:
						<div style={{height: rows?.length? `${rows.length * vals.pxRow}px` : '100%',       // Full height when empty, so emptyVal (error/empty message) can center in the grid area.
							minWidth: totalColWidth + 'px',
							boxSizing: 'border-box',
							paddingTop: `${vals.startIndex * vals.pxRow}px`,
							contain: 'inline-size'}}>
							{rows?.length? [...rows.slice(vals.startIndex, vals.stopIndex)]         // convert sparse cells into undefined cells, so map will iterate all items.
								.map((rowData, index) => rowData === undefined?
									<div key={index} style={style}/>
									:
									rowRenderer({style, rowData, index: vals.startIndex + index}))
								:
								funcOrValOr(emptyVal)
							}
						</div>
				}
			</div>
		</div>
	);
}
